/**
 * MED-971 / E2.6c — client-side resolution of the recap-email default template's
 * three session-level merge tags: `{{coach_name}}`, `{{session_number}}`, and
 * `{{next_session_date}}`. These are resolved on load so the coach sees a fully
 * populated template, with empty placeholders only where the ticket calls for
 * manual entry (bullets and resources).
 *
 * MED-1252 — the same resolver now also resolves the default subject's
 * session-level tags (`{{session_number}}`, `{{cohort_name}}`) for the
 * read-only RecapEmailDraftCard. `{{cohort_name}}` is opt-in via the optional
 * `cohortName` context field so body-seed callers are unaffected. The Send
 * modal still shows the subject with raw, editable tokens.
 *
 * Per-recipient tags (`{{first_name}}`, etc.) are not resolved here either —
 * they're resolved server-side at send time so each participant's copy gets
 * their own name. Leaving them as literal `{{first_name}}` text in the editor
 * is intentional: the coach can see what will be substituted.
 *
 * Null inputs resolve to empty string — consistent with the server-side resolver
 * in `medley-api/utils/recapEmailMergeTags.ts`.
 */

const SESSION_DATE_DISPLAY_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

function formatSessionDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', SESSION_DATE_DISPLAY_FORMAT_OPTIONS);
}

/**
 * MED-901 / E2.16 — the default recap-email subject line. Single source of
 * truth shared by the Send modal (editable, raw tokens) and the read-only
 * RecapEmailDraftCard (resolved for display — MED-1252). Keeping it here means
 * the card can seed the same default the modal applies without duplicating the
 * literal.
 */
export const DEFAULT_RECAP_SUBJECT = 'Session {{session_number}} Recap — {{cohort_name}}';

export interface TemplateMergeTagContext {
  coachName: string;
  sessionNumber: number | null;
  /** ISO 8601 — formatted "MMM d, yyyy" before substitution */
  nextSessionDateIso: string | null;
  /**
   * MED-1252 — optional cohort name. `{{cohort_name}}` is added to the tag map
   * ONLY when this is provided, so existing body-seed callers (which omit it)
   * leave `{{cohort_name}}` untouched for server-side resolution, while the
   * subject seed passes it to resolve `{{cohort_name}}` for read-only display.
   */
  cohortName?: string;
}

export function resolveTemplateMergeTags(
  template: string,
  ctx: TemplateMergeTagContext,
): string {
  const map: Record<string, string> = {
    '{{coach_name}}': ctx.coachName ?? '',
    '{{session_number}}': ctx.sessionNumber != null ? String(ctx.sessionNumber) : '',
    '{{next_session_date}}': formatSessionDate(ctx.nextSessionDateIso),
  };
  // Only resolve `{{cohort_name}}` when the caller opts in — omitting it keeps
  // the body-seed behavior identical (tag left literal, resolved server-side).
  if (ctx.cohortName !== undefined) {
    map['{{cohort_name}}'] = ctx.cohortName;
  }
  let output = template;
  for (const [tag, value] of Object.entries(map)) {
    output = output.split(tag).join(value);
  }
  return output;
}
